// COMP-ANOS (2026-07-16): aba "Ano × Ano" do Dashboard Comercial — contrasta
// dois anos (default: ano passado × ano corrente) nas métricas pedidas pelo
// Lucas: numero de propostas, numero de vendas, ticket medio e valor total
// vendido. Reusa GET /comercial/dashboard (ano= e ini/fim custom) — 3 chamadas:
// ano A cheio, ano B, e ano A no MESMO PERIODO do ano B (comparacao justa
// quando o ano corrente ainda esta no meio).
// Definicoes (as mesmas do dashboard): proposta = criada no periodo
// (data_abertura); venda = status ganho por data_fechamento; valor = COALESCE
// (valor_final, valor_estimado) — hibrido venda local x Ploomes.
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Card, Row, Col, Statistic, Table, Typography, Spin, Select, Space, Tag, Alert, Flex,
} from 'antd';
import { RiseOutlined, FallOutlined, SwapOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';

const { Text, Title } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

const fmtR$ = (v: number) =>
  'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtCompacto = (v: number): string => {
  const n = v || 0;
  if (n >= 1_000_000) return 'R$ ' + (n / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + 'M';
  if (n >= 1_000)     return 'R$ ' + Math.round(n / 1_000).toLocaleString('pt-BR') + 'k';
  return 'R$ ' + Math.round(n).toLocaleString('pt-BR');
};

interface ResumoAno {
  propostas: number;
  vendas: number;
  ticket: number;
  valor: number;
  serie: any[];
}

function resumoDe(payload: any): ResumoAno {
  const serie = payload?.serie_mensal || [];
  return {
    propostas: serie.reduce((a: number, b: any) => a + (b.propostas_criadas || 0), 0),
    vendas: payload?.receita?.n || 0,
    ticket: payload?.receita?.ticket_medio || 0,
    valor: payload?.receita?.total || 0,
    serie,
  };
}

const ANOS: number[] = (() => {
  const atual = new Date().getFullYear();
  const arr: number[] = [];
  for (let y = atual; y >= 2020; y--) arr.push(y);
  return arr;
})();

export default function ComparativoAnos() {
  const hoje = new Date();
  const [anoA, setAnoA] = useState(hoje.getFullYear() - 1);
  const [anoB, setAnoB] = useState(hoje.getFullYear());
  const [dados, setDados] = useState<{ a: ResumoAno; b: ResumoAno; aMesmoPeriodo: ResumoAno | null } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // "Mesmo período": ano B corrente termina hoje → compara A até o mesmo dia/mês.
  const anoBParcial = anoB === hoje.getFullYear();
  const mmdd = `${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`;

  useEffect(() => {
    (async () => {
      setLoading(true); setErro(null);
      try {
        const token = await getToken();
        const h = { headers: { Authorization: token } };
        const calls = [
          axios.get(`${API_URL}/comercial/dashboard?ano=${anoA}&granularidade=mes`, h),
          axios.get(`${API_URL}/comercial/dashboard?ano=${anoB}&granularidade=mes`, h),
        ];
        if (anoBParcial) {
          calls.push(axios.get(`${API_URL}/comercial/dashboard?ini=${anoA}-01-01&fim=${anoA}-${mmdd}&granularidade=mes`, h));
        }
        const rs = await Promise.all(calls);
        setDados({
          a: resumoDe(rs[0].data),
          b: resumoDe(rs[1].data),
          aMesmoPeriodo: anoBParcial ? resumoDe(rs[2].data) : null,
        });
      } catch (e: any) {
        setErro(e?.response?.data?.erro || e.message || 'Falha ao carregar');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anoA, anoB]);

  const linhasMes = useMemo(() => {
    if (!dados) return [];
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const de = (serie: any[], ym: string) => serie.find((s) => s.mes === ym) || {};
    return meses.map((nome, i) => {
      const mm = String(i + 1).padStart(2, '0');
      const a = de(dados.a.serie, `${anoA}-${mm}`);
      const b = de(dados.b.serie, `${anoB}-${mm}`);
      return {
        key: mm, mes: nome,
        propA: a.propostas_criadas || 0, propB: b.propostas_criadas || 0,
        vendA: a.ganhos_n || 0, vendB: b.ganhos_n || 0,
        valA: a.receita_ganha || 0, valB: b.receita_ganha || 0,
      };
    });
  }, [dados, anoA, anoB]);

  if (erro) return <Alert type="error" message="Não consegui carregar o comparativo" description={erro} style={{ margin: 24 }} />;
  if (loading || !dados) return <Flex justify="center" align="center" style={{ minHeight: 240 }}><Spin size="large" /></Flex>;

  const { a, b, aMesmoPeriodo } = dados;
  // Delta vs MESMO PERIODO quando o ano B está incompleto (justo); senão vs ano cheio.
  const ref = aMesmoPeriodo || a;

  const deltaTag = (atual: number, anterior: number, dinheiro = false) => {
    if (!anterior) return <Tag>sem base {anoA}</Tag>;
    const d = ((atual - anterior) / anterior) * 100;
    const cor = d >= 0 ? 'green' : 'red';
    const Icone = d >= 0 ? RiseOutlined : FallOutlined;
    return (
      <Tag color={cor} icon={<Icone />}>
        {d >= 0 ? '+' : ''}{d.toFixed(1)}% vs {anoA}{aMesmoPeriodo ? ' (mesmo período)' : ''}
      </Tag>
    );
  };

  const METRICAS = [
    { titulo: 'Propostas criadas', vB: b.propostas, vRef: ref.propostas, vAno: a.propostas, dinheiro: false },
    { titulo: 'Vendas (negócios ganhos)', vB: b.vendas, vRef: ref.vendas, vAno: a.vendas, dinheiro: false },
    { titulo: 'Ticket médio', vB: b.ticket, vRef: ref.ticket, vAno: a.ticket, dinheiro: true },
    { titulo: 'Valor total vendido', vB: b.valor, vRef: ref.valor, vAno: a.valor, dinheiro: true },
  ];

  const colGrupo = (titulo: string, kA: string, kB: string, dinheiro = false) => ({
    title: titulo,
    children: [
      { title: String(anoA), dataIndex: kA, key: kA, align: 'right' as const,
        render: (v: number) => <Text type="secondary">{dinheiro ? fmtCompacto(v) : v || '—'}</Text> },
      { title: String(anoB), dataIndex: kB, key: kB, align: 'right' as const,
        render: (v: number, r: any) => {
          const va = r[kA] || 0;
          const cor = v > va ? '#0a7f4a' : v < va ? '#c4453c' : undefined;
          return <Text strong style={{ color: cor }}>{dinheiro ? fmtCompacto(v) : v || '—'}</Text>;
        } },
    ],
  });

  return (
    <div style={{ padding: '16px 24px' }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ marginBottom: 14 }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#020D7F' }}><SwapOutlined /> {anoA} × {anoB}</Title>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Propostas por data de criação · vendas por data de fechamento · valor = valor final da venda.
            {aMesmoPeriodo && <> Deltas comparam com o <b>mesmo período</b> de {anoA} (até {hoje.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}).</>}
          </Text>
        </div>
        <Space size={6}>
          <Select size="small" style={{ width: 90 }} value={anoA}
            onChange={setAnoA} options={ANOS.map((y) => ({ value: y, label: y }))} />
          <Text type="secondary">×</Text>
          <Select size="small" style={{ width: 90 }} value={anoB}
            onChange={setAnoB} options={ANOS.map((y) => ({ value: y, label: y }))} />
        </Space>
      </Flex>

      <Row gutter={[12, 12]}>
        {METRICAS.map((m) => (
          <Col xs={24} sm={12} lg={6} key={m.titulo}>
            <Card size="small">
              <Statistic title={`${m.titulo} — ${anoB}`}
                value={m.dinheiro ? fmtCompacto(m.vB) : m.vB} />
              <div style={{ marginTop: 8 }}>{deltaTag(m.vB, m.vRef, m.dinheiro)}</div>
              <div style={{ marginTop: 6 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {anoA} ano cheio: <b>{m.dinheiro ? fmtCompacto(m.vAno) : m.vAno}</b>
                  {aMesmoPeriodo && <> · mesmo período: <b>{m.dinheiro ? fmtCompacto(m.vRef) : m.vRef}</b></>}
                </Text>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card size="small" style={{ marginTop: 16 }} title={`Mês a mês — ${anoA} × ${anoB}`}>
        <Table
          dataSource={linhasMes}
          pagination={false}
          size="small"
          scroll={{ x: true }}
          columns={[
            { title: 'Mês', dataIndex: 'mes', key: 'mes', fixed: 'left' as const },
            colGrupo('Propostas', 'propA', 'propB'),
            colGrupo('Vendas', 'vendA', 'vendB'),
            colGrupo('Valor vendido', 'valA', 'valB', true),
          ] as any}
          summary={(rows) => {
            const soma = (k: string) => (rows as any[]).reduce((acc, r) => acc + (r[k] || 0), 0);
            return (
              <Table.Summary.Row style={{ fontWeight: 600, background: '#fafafa' }}>
                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">{soma('propA')}</Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">{soma('propB')}</Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">{soma('vendA')}</Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">{soma('vendB')}</Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">{fmtCompacto(soma('valA'))}</Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">{fmtCompacto(soma('valB'))}</Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          O ticket médio de cada ano está nos cards acima (valor vendido ÷ vendas). Meses futuros de {anoB} aparecem zerados.
        </Text>
      </Card>
    </div>
  );
}
